<script setup>
import { ref, onMounted, watch } from 'vue';
import { useTransactionsStore } from '@/stores/transactions';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const params = ref({
    term: '',
    page: 1,
    per_page: 10,
    sort_by: null,
    global_status: null,
});

const optionsStore = useOptionsStore();
const statusOptions = ref([{ label: 'All', value: null }]);

const transactionsStore = useTransactionsStore();
const { allTransactions } = storeToRefs(transactionsStore);

const fetchTransactions = () => {
    transactionsStore.searchTransactions({ ...params.value });
};

const clearSearch = () => {
    params.value.term = '';
    fetchTransactions();
};

const tableUpdate = ({ page, sortBy, itemsPerPage, sortDesc }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage, sort_desc: sortDesc });
    fetchTransactions();
};

onMounted(() => {
    fetchTransactions();
    // Append dynamic transaction statuses (slug & name) when hydrated
    if (optionsStore.transactionStatuses?.length) {
        statusOptions.value = [
            { label: 'All', value: null },
            ...optionsStore.transactionStatuses.map(s => ({ label: s.name, value: s.slug }))
        ];
    }
});

watch(() => optionsStore.transactionStatuses, (list) => {
    if (list && list.length) {
        statusOptions.value = [
            { label: 'All', value: null },
            ...list.map(s => ({ label: s.name, value: s.slug }))
        ];
    }
}, { deep: true });
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('transaction.transaction_management') }}</template>

    <div class="grid lg:grid-cols-4 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="fetchTransactions"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />

        <StyledSelect
            v-model="params.global_status"
            :items="statusOptions"
            item-title="label"
            item-value="value"
            :label="$t('label.status')"
            @change="fetchTransactions" />
    </div>

    <DataTable
        v-if="allTransactions"
        :data="allTransactions"
        :headers="[
            { title: $t('transaction.transaction_number') || 'Transaction #', key: 'transaction_number', value: i => i.transaction_number, sortable: false },
            { title: $t('label.amount') || 'Amount', key: 'amount', value: i => i.amount, sortable: false },
            { title: $t('label.company') || 'Company', key: 'company_name', value: i => i.created_by?.company_name || '', sortable: false },
            { title: $t('agent.agent') || 'Agent', key: 'agent', value: i => (i.payment_method?.accountable?.first_name || '') + ' ' + (i.payment_method?.accountable?.last_name || ''), sortable: false },
            { title: $t('label.status') || 'Status', key: 'status', value: i => i.global_status?.name || '', sortable: false },
            { title: $t('label.created_at') || 'Created', key: 'created_at', value: i => i.created_at ? new Date(i.created_at).toLocaleString() : '', sortable: false },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
